import React, { useState, useRef, useEffect } from 'react';
import { ControlDef, ArrayItemField } from './types';
import { Trash2, Plus, Camera, ChevronUp, ChevronDown, FolderOpen, ExternalLink } from 'lucide-react';
import PexelsImagePicker from './PexelsImagePicker';
import IconPickerField from './IconPickerField';
import MediaLibraryModal from './MediaLibraryModal';
import RichTextControl from './RichTextControl';
import { getAllLeadForms } from '../../services/api';

const getApiBase = () =>
  process.env.REACT_APP_API_URL || `${window.location.protocol}//${window.location.hostname}:8001`;

interface Props {
  controlKey: string;
  controlDef: ControlDef;
  value: any;
  onChange: (value: any) => void;
  brandLogos?: { label: string; url: string }[];
}

const ControlRenderer: React.FC<Props> = ({ controlKey, controlDef, value, onChange, brandLogos = [] }) => {
  const label = controlDef.label || controlKey.replace(/_/g, ' ');
  const [showPexelsPicker, setShowPexelsPicker] = useState(false);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0',
    borderRadius: 6, fontSize: '0.8125rem', background: '#fff',
    outline: 'none', boxSizing: 'border-box',
  };

  switch (controlDef.type) {
    case 'text':
    case 'url':
    case 'color':
      return (
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
            {label}
          </label>
          <input
            type={controlDef.type === 'color' ? 'color' : 'text'}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            placeholder={controlDef.ai_hint || ''}
            maxLength={controlDef.max_length || undefined}
            style={controlDef.type === 'color' ? { ...inputStyle, padding: 2, height: 36 } : inputStyle}
          />
        </div>
      );

    case 'image':
      return (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
              {label}
            </label>
            {/* URL input */}
            <input
              type="text"
              value={value || ''}
              onChange={e => onChange(e.target.value)}
              placeholder={controlDef.ai_hint || 'Image URL'}
              maxLength={controlDef.max_length || undefined}
              style={{ ...inputStyle, marginBottom: 6 }}
            />
            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                title="Open media library"
                onClick={() => setShowMediaLibrary(true)}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  padding: '6px 0', border: '1px solid #e2e8f0', borderRadius: 6,
                  background: '#fff', cursor: 'pointer', color: '#475569',
                  fontSize: '0.75rem', fontWeight: 600,
                }}
              >
                <FolderOpen size={14} /> Library
              </button>
              <button
                type="button"
                title="Browse Pexels stock photos"
                onClick={() => setShowPexelsPicker(true)}
                style={{
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  padding: '6px 0', border: '1px solid #e2e8f0', borderRadius: 6,
                  background: '#fff', cursor: 'pointer', color: '#2177FF',
                  fontSize: '0.75rem', fontWeight: 600,
                }}
              >
                <Camera size={14} /> Stock Photos
              </button>
            </div>

            {/* Thumbnail preview */}
            {value && typeof value === 'string' && value.startsWith('http') && (
              <div style={{ marginTop: 6 }}>
                <img
                  src={value}
                  alt="Preview"
                  style={{ width: '100%', maxHeight: 100, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }}
                  onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                />
              </div>
            )}
          </div>

          {/* Brand guide logo shortcuts */}
          {brandLogos.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Brand Assets
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {brandLogos.map(logo => (
                  <button
                    key={logo.url}
                    type="button"
                    title={logo.label}
                    onClick={() => onChange(logo.url)}
                    style={{
                      padding: 3,
                      border: value === logo.url ? '2px solid #2177FF' : '1px solid #e2e8f0',
                      borderRadius: 6,
                      background: value === logo.url ? 'rgba(33,119,255,0.07)' : '#f8fafc',
                      cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 48, height: 36, overflow: 'hidden',
                    }}
                  >
                    <img
                      src={logo.url}
                      alt={logo.label}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {showMediaLibrary && (
            <MediaLibraryModal
              currentUrl={value || ''}
              onSelect={url => { onChange(url); setShowMediaLibrary(false); }}
              onClose={() => setShowMediaLibrary(false)}
            />
          )}
          {showPexelsPicker && (
            <PexelsImagePicker
              initialQuery={controlDef.ai_hint || label}
              onSelect={url => onChange(url)}
              onClose={() => setShowPexelsPicker(false)}
            />
          )}
        </>
      );

    case 'textarea':
      return (
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
            {label}
          </label>
          <textarea
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            placeholder={controlDef.ai_hint || ''}
            rows={3}
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>
      );

    case 'richtext':
      // Allowlisted formatted text (Host-Styled Text body). No raw-HTML mode by design.
      return (
        <RichTextControl
          label={label}
          value={typeof value === 'string' ? value : ''}
          onChange={onChange}
          placeholder={controlDef.ai_hint || undefined}
        />
      );

    case 'form_select':
      return <FormSelectControl label={label} value={value} onChange={onChange} />;

    case 'select':
      return (
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
            {label}
          </label>
          <select value={value || ''} onChange={e => onChange(e.target.value)} style={inputStyle}>
            <option value="">Select...</option>
            {(controlDef.options || []).map(opt => (
              <option key={opt} value={opt}>{opt.replace(/_/g, ' ')}</option>
            ))}
          </select>
        </div>
      );

    case 'boolean':
      return (
        <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={!!value}
            onChange={e => onChange(e.target.checked)}
            style={{ width: 16, height: 16 }}
          />
          <label style={{ fontSize: '0.8125rem', color: '#475569' }}>{label}</label>
        </div>
      );

    case 'list':
      return <ListControl controlKey={controlKey} controlDef={controlDef} value={value} onChange={onChange} label={label} />;

    case 'array': {
      // Normalise value: parse JSON strings, fall back to empty array
      let arrValue: Record<string, any>[];
      if (typeof value === 'string') {
        try { arrValue = JSON.parse(value); } catch { arrValue = []; }
      } else {
        arrValue = Array.isArray(value) ? value : [];
      }
      const itemSchema = Array.isArray(controlDef.item_schema) ? controlDef.item_schema as ArrayItemField[] : undefined;
      return (
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
            {label}
          </label>
          <ArrayControlEditor value={arrValue} itemSchema={itemSchema} onChange={onChange} />
        </div>
      );
    }

    default:
      return (
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
            {label} ({controlDef.type})
          </label>
          <input type="text" value={String(value || '')} onChange={e => onChange(e.target.value)} style={inputStyle} />
        </div>
      );
  }
};

// ── ArrayControlEditor ──────────────────────────────────────────────────────

interface ArrayControlEditorProps {
  value: Record<string, any>[];
  itemSchema?: ArrayItemField[];
  onChange: (newValue: Record<string, any>[]) => void;
}

const ArrayControlEditor: React.FC<ArrayControlEditorProps> = ({ value, itemSchema, onChange }) => {
  const items = Array.isArray(value) ? value : [];

  const updateField = (idx: number, key: string, val: string) => {
    const updated = items.map((item, i) => i === idx ? { ...item, [key]: val } : item);
    onChange(updated);
  };

  const removeItem = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  const moveItem = (idx: number, direction: 'up' | 'down') => {
    const next = [...items];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    onChange(next);
  };

  const addItem = () => {
    if (itemSchema && itemSchema.length > 0) {
      const newItem: Record<string, string> = {};
      itemSchema.forEach(f => { newItem[f.key] = ''; });
      onChange([...items, newItem]);
    } else {
      onChange([...items, {}]);
    }
  };

  const handleJsonBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    try {
      const parsed = JSON.parse(e.target.value);
      if (Array.isArray(parsed)) onChange(parsed);
    } catch {
      // Invalid JSON — ignore
    }
  };

  const iconBtnStyle: React.CSSProperties = {
    border: 'none', background: 'none', cursor: 'pointer',
    padding: 2, color: '#94a3b8', display: 'flex', alignItems: 'center',
  };

  // Fallback: no schema → raw JSON textarea
  if (!itemSchema || itemSchema.length === 0) {
    return (
      <div className="sb-array-editor">
        <textarea
          defaultValue={JSON.stringify(items, null, 2)}
          onBlur={handleJsonBlur}
          rows={6}
          style={{ width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: '0.75rem', background: '#fff', resize: 'vertical', fontFamily: 'monospace', boxSizing: 'border-box' as const }}
        />
      </div>
    );
  }

  return (
    <div className="sb-array-editor">
      {items.map((item, idx) => (
        <div key={idx} className="sb-array-item">
          <div className="sb-array-item-header">
            <span style={{ fontSize: '0.7rem', color: '#94a3b8', flex: 1 }}>#{idx + 1}</span>
            <button style={iconBtnStyle} onClick={() => moveItem(idx, 'up')} disabled={idx === 0} title="Move up">
              <ChevronUp size={13} />
            </button>
            <button style={iconBtnStyle} onClick={() => moveItem(idx, 'down')} disabled={idx === items.length - 1} title="Move down">
              <ChevronDown size={13} />
            </button>
            <button style={{ ...iconBtnStyle, color: '#ef4444' }} onClick={() => removeItem(idx)} title="Remove item">
              <Trash2 size={13} />
            </button>
          </div>
          {itemSchema.map(field => (
            <div key={field.key} className="sb-array-item-field">
              <label>{field.label}</label>
              {field.key === 'icon' ? (
                <IconPickerField
                  value={item[field.key] || ''}
                  onChange={v => updateField(idx, field.key, v)}
                  placeholder="icon name"
                />
              ) : field.type === 'textarea' ? (
                <textarea
                  value={item[field.key] || ''}
                  onChange={e => updateField(idx, field.key, e.target.value)}
                  rows={2}
                />
              ) : (
                <input
                  type="text"
                  value={item[field.key] || ''}
                  onChange={e => updateField(idx, field.key, e.target.value)}
                  placeholder={field.label}
                />
              )}
            </div>
          ))}
        </div>
      ))}
      <button className="sb-array-add-btn" onClick={addItem}>
        <Plus size={13} /> Add item
      </button>
    </div>
  );
};

// ── ListItemImageField ─────────────────────────────────────────────────────

const ListItemImageField: React.FC<{ value: string; onChange: (url: string) => void; placeholder?: string }> = ({ value, onChange, placeholder }) => {
  const [showPexels, setShowPexels] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);

  return (
    <div>
      {/* URL input — full width */}
      <input
        type="text"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder || 'Image URL'}
        style={{
          width: '100%', padding: '4px 8px', border: '1px solid #e2e8f0',
          borderRadius: 4, fontSize: '0.75rem', background: '#fff',
          outline: 'none', boxSizing: 'border-box' as const, marginBottom: 4,
        }}
      />
      {/* Action buttons row */}
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          type="button"
          title="Open media library"
          onClick={() => setShowLibrary(true)}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
            padding: '5px 0', border: '1px solid #e2e8f0', borderRadius: 4,
            background: '#fff', cursor: 'pointer', color: '#475569', fontSize: '0.7rem', fontWeight: 600,
          }}
        >
          <FolderOpen size={12} /> Library
        </button>
        <button
          type="button"
          title="Browse Pexels stock photos"
          onClick={() => setShowPexels(true)}
          style={{
            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 3,
            padding: '5px 0', border: '1px solid #e2e8f0', borderRadius: 4,
            background: '#fff', cursor: 'pointer', color: '#2177FF', fontSize: '0.7rem', fontWeight: 600,
          }}
        >
          <Camera size={12} /> Stock
        </button>
      </div>
      {value && typeof value === 'string' && value.startsWith('http') && (
        <img
          src={value}
          alt="Preview"
          style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '50%', border: '1px solid #e2e8f0', marginTop: 4 }}
          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
        />
      )}
      {showLibrary && (
        <MediaLibraryModal
          currentUrl={value || ''}
          onSelect={url => { onChange(url); setShowLibrary(false); }}
          onClose={() => setShowLibrary(false)}
        />
      )}
      {showPexels && (
        <PexelsImagePicker
          initialQuery="person portrait professional"
          onSelect={url => { onChange(url); setShowPexels(false); }}
          onClose={() => setShowPexels(false)}
        />
      )}
    </div>
  );
};

// ── ListControl (legacy dict-schema) ────────────────────────────────────────

const ListControl: React.FC<Props & { label: string }> = ({ controlDef, value, onChange, label }) => {
  const items = Array.isArray(value) ? value : [];
  // item_schema for 'list' type is always a dict (Record<string,string>)
  const schema = (!Array.isArray(controlDef.item_schema) && controlDef.item_schema) ? controlDef.item_schema as Record<string, string> : {};
  const schemaKeys = Object.keys(schema);
  const maxItems = controlDef.max_items || 10;

  const updateItem = (idx: number, key: string, val: string) => {
    const updated = items.map((item, i) => i === idx ? { ...item, [key]: val } : item);
    onChange(updated);
  };

  const addItem = () => {
    if (items.length >= maxItems) return;
    const newItem: Record<string, string> = {};
    schemaKeys.forEach(k => { newItem[k] = ''; });
    onChange([...items, newItem]);
  };

  const removeItem = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx));
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
        {label} ({items.length}/{maxItems})
      </label>
      {items.map((item, idx) => (
        <div key={idx} style={{
          border: '1px solid #e2e8f0', borderRadius: 6, padding: 8,
          marginBottom: 6, background: '#fafafa', position: 'relative',
          overflow: 'hidden',
        }}>
          <button
            onClick={() => removeItem(idx)}
            style={{
              position: 'absolute', top: 4, right: 4, border: 'none',
              background: 'none', cursor: 'pointer', padding: 2, color: '#94a3b8',
            }}
          ><Trash2 size={12} /></button>
          {schemaKeys.map(key => (
            <div key={key} style={{ marginBottom: 4 }}>
              <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginBottom: 2, textTransform: 'capitalize' }}>
                {key.replace(/_/g, ' ')}
              </div>
              {key === 'icon' ? (
                <IconPickerField
                  value={item[key] || ''}
                  onChange={v => updateItem(idx, key, v)}
                  placeholder="icon name"
                />
              ) : schema[key] === 'image' ? (
                <ListItemImageField
                  value={item[key] || ''}
                  onChange={v => updateItem(idx, key, v)}
                  placeholder="Avatar URL"
                />
              ) : (
                <input
                  type="text"
                  placeholder={key.replace(/_/g, ' ')}
                  value={item[key] || ''}
                  onChange={e => updateItem(idx, key, e.target.value)}
                  style={{
                    width: '100%', padding: '4px 8px', border: '1px solid #e2e8f0',
                    borderRadius: 4, fontSize: '0.75rem', background: '#fff',
                    outline: 'none', boxSizing: 'border-box',
                  }}
                />
              )}
            </div>
          ))}
        </div>
      ))}
      {items.length < maxItems && (
        <button onClick={addItem} style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '4px 10px', border: '1px dashed #cbd5e1', borderRadius: 6,
          background: 'transparent', cursor: 'pointer', fontSize: '0.75rem', color: '#64748b',
        }}>
          <Plus size={12} /> Add item
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// FormSelectControl — fetches all user lead capture forms and renders a dropdown
// ---------------------------------------------------------------------------
interface LeadFormOption { form_id: string; name: string; }

const FormSelectControl: React.FC<{ label: string; value: any; onChange: (v: string) => void }> = ({ label, value, onChange }) => {
  const [forms, setForms] = useState<LeadFormOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getAllLeadForms()
      .then((data: LeadFormOption[]) => { setForms(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0',
    borderRadius: 6, fontSize: '0.8125rem', background: '#fff',
    outline: 'none', boxSizing: 'border-box',
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
        {label}
      </label>
      {loading ? (
        <div style={{ fontSize: '0.75rem', color: '#94a3b8', padding: '6px 0' }}>Loading forms…</div>
      ) : forms.length === 0 ? (
        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: 6 }}>
          No forms found.{' '}
          <a href="/lead-capture" target="_blank" rel="noreferrer"
            style={{ color: '#2177FF', fontWeight: 600, textDecoration: 'none' }}>
            Create one in Lead Capture <ExternalLink size={10} style={{ verticalAlign: 'middle' }} />
          </a>
        </div>
      ) : (
        <select value={value || ''} onChange={e => onChange(e.target.value)} style={inputStyle}>
          <option value="">— Select a form —</option>
          {forms.map(f => (
            <option key={f.form_id} value={f.form_id}>{f.name}</option>
          ))}
        </select>
      )}
      {forms.length > 0 && (
        <a href="/lead-capture" target="_blank" rel="noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.7rem', color: '#64748b', marginTop: 4, textDecoration: 'none' }}>
          Manage forms in Lead Capture <ExternalLink size={10} />
        </a>
      )}
    </div>
  );
};

export default ControlRenderer;
